import React, { useState, useEffect } from 'react';
import {
  Box,
  Popover,
  Typography,
  Button,
  Select,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * PeriodRangePopover - A popover for selecting a custom period range
 *
 * @param {object} props
 * @param {HTMLElement} props.anchorEl - Anchor element for the popover
 * @param {boolean} props.open - Whether the popover is open
 * @param {function} props.onClose - Callback when popover closes
 * @param {Array<{period: string}>} props.periods - Array of available periods
 * @param {function} props.onApply - Callback with { start, end } when user applies selection
 * @param {string} props.initialStart - Initial start period (optional)
 * @param {string} props.initialEnd - Initial end period (optional)
 */
const PeriodRangePopover = ({
  anchorEl,
  open,
  onClose,
  periods = [],
  onApply,
  initialStart,
  initialEnd,
}) => {
  const theme = useTheme();
  const popoverBackground = theme.background?.transparent || theme.palette.background.paper;

  // Local state for the dropdowns
  const [startPeriod, setStartPeriod] = useState(initialStart || '');
  const [endPeriod, setEndPeriod] = useState(initialEnd || '');

  // Reset when popover opens
  useEffect(() => {
    if (open) {
      setStartPeriod(initialStart || (periods.length > 0 ? periods[0].period : ''));
      setEndPeriod(initialEnd || (periods.length > 0 ? periods[periods.length - 1].period : ''));
    }
  }, [open, periods, initialStart, initialEnd]);

  // Get index of a period in the array
  const getPeriodIndex = (period) => {
    return periods.findIndex(p => p.period === period);
  };

  // Filter "To" options to only show periods >= "From" selection
  const availableEndPeriods = startPeriod
    ? periods.filter((_, idx) => idx >= getPeriodIndex(startPeriod))
    : periods;

  // Ensure endPeriod is valid when startPeriod changes
  useEffect(() => {
    if (startPeriod && endPeriod) {
      const startIdx = getPeriodIndex(startPeriod);
      const endIdx = getPeriodIndex(endPeriod);
      if (endIdx < startIdx) {
        // Reset end to start if it's before start
        setEndPeriod(startPeriod);
      }
    }
  }, [startPeriod]);

  const handleApply = () => {
    if (startPeriod && endPeriod && onApply) {
      onApply({ start: startPeriod, end: endPeriod });
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const selectSx = {
    height: '32px',
    fontSize: '12px',
    backgroundColor: theme.tableHeader.highlight,
    '& .MuiSelect-select': {
      py: 0.5,
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: popoverBackground,
        backdropFilter: 'blur(8px)',
        maxHeight: 200,
      },
    },
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          mt: 1,
          p: 2,
          minWidth: 240,
          backgroundColor: popoverBackground,
          backdropFilter: 'blur(8px)',
          borderRadius: '8px',
        },
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          color: theme.palette.text.primary,
          fontWeight: 600,
        }}
      >
        Select Period Range
      </Typography>

      {/* From dropdown */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.5,
            color: theme.palette.text.secondary,
            fontSize: '10px',
          }}
        >
          From
        </Typography>
        <Select
          value={startPeriod}
          onChange={(e) => setStartPeriod(e.target.value)}
          size="small"
          fullWidth
          sx={selectSx}
          MenuProps={menuProps}
        >
          {periods.map((p) => (
            <MenuItem key={p.period} value={p.period} sx={{ fontSize: '12px' }}>
              {p.period}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* To dropdown */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mb: 0.5,
            color: theme.palette.text.secondary,
            fontSize: '10px',
          }}
        >
          To
        </Typography>
        <Select
          value={endPeriod}
          onChange={(e) => setEndPeriod(e.target.value)}
          size="small"
          fullWidth
          sx={selectSx}
          MenuProps={menuProps}
        >
          {availableEndPeriods.map((p) => (
            <MenuItem key={p.period} value={p.period} sx={{ fontSize: '12px' }}>
              {p.period}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          size="small"
          onClick={handleCancel}
          sx={{
            fontSize: '11px',
            color: theme.palette.text.secondary,
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleApply}
          disabled={!startPeriod || !endPeriod}
          sx={{ fontSize: '11px' }}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default PeriodRangePopover;
